// Authentication Service
import axios from 'axios'
import { config } from '@/config'
import { ensureApiV1Base } from '@/config/apiBase'
import { shouldAttemptCookieRefresh } from '@/utils/shouldAttemptCookieRefresh'
import { attachSupplierAxiosPerf } from '@/platforms/supplier/perf/supplierPerfDiagnostics'

// Hamesha exactly .../api/v1. Empty → fail closed in prod (TA-090).
const API_BASE = ensureApiV1Base(config.SUPPLIER_API_BASE_URL || config.API_BASE_URL)

function isNetworkError(error) {
  return (
    !error.response &&
    (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || error.message === 'Network Error' || error.message?.includes('timeout'))
  )
}

function networkErrorMessage(action = 'request') {
  return `Cannot reach server at ${API_BASE || '(API base not configured)'}. Set NEXT_PUBLIC_SUPPLIER_API_BASE_URL (or LOCAL/LIVE pair) in .env.`
}

/** GET /supplier/auth/me — Phase 3 perf: allow slow proxy/cold starts without false 5s failures. */
export const SUPPLIER_ME_TIMEOUT_MS = 8000

function isRequestAborted(error) {
  return (
    axios.isCancel?.(error) ||
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError'
  )
}

function authApiErrorMessage(error, fallback) {
  const d = error?.response?.data
  const status = error?.response?.status
  if (d == null) return fallback
  if (typeof d === 'string') return d.trim() || fallback
  const msg = d.detail || d.message || (typeof d.error === 'string' ? d.error : null)
  if (msg && String(msg).trim()) return String(msg).trim()
  if (status === 404) {
    return 'API path not found (404). Deploy or restart the backend with the latest code, and set NEXT_PUBLIC_SUPPLIER_API_BASE_URL to your server’s /api/v1 base.'
  }
  return fallback
}

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 5000,
  withCredentials: true,
})

const authApi = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 12000,
  withCredentials: true,
})

/** In-memory session flag — not a JWT. Set by AuthContext after /me or login. */
let sessionActive = false

/** Single-flight promise to prevent concurrent duplicate /me calls */
let currentUserRequestPromise = null

function stripLegacyJwtKeys() {
  try {
    localStorage.removeItem('supplier_access_token')
    localStorage.removeItem('supplier_refresh_token')
  } catch (_) {}
}

/** Clear supplier UI/session caches on logout (auth cookies cleared by API). */
function clearSupplierLocalData() {
  try {
    const exact = [
      'supplier_access_token',
      'supplier_refresh_token',
      'supplier_id',
      'account_settings',
      'supplier_profile',
      'supplier_onboarding_status',
      'supplier_blocked_reason',
      'supplier_rejection_reason',
      'supplier_dashboard_cache',
      'supplier_dashboard_cache_ts',
      'notifications',
      'persist:tripsadora_chat',
    ]
    exact.forEach((k) => {
      localStorage.removeItem(k)
      sessionStorage.removeItem(k)
    })
    const prefixKeys = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key) continue
      if (
        key.startsWith('supplier_onboarding_progress_') ||
        key.startsWith('supplier_dashboard_') ||
        key.startsWith('supplier_onboarding_upload_token')
      ) {
        prefixKeys.push(key)
      }
    }
    prefixKeys.forEach((k) => localStorage.removeItem(k))
    try {
      sessionStorage.removeItem('supplier_onboarding_upload_token')
      localStorage.removeItem('supplier_onboarding_upload_token')
      // Switch shell to customer after real supplier logout (not browser Back)
      localStorage.setItem('tripsadora_mode', 'customer')
    } catch (_) {}
  } catch (_) {}
}

const storeSupplierId = (supplierCode) => {
  if (!supplierCode) return
  try {
    localStorage.setItem('supplier_id', supplierCode)
    sessionStorage.setItem('supplier_id', supplierCode)
  } catch (_) {}
}

let refreshPromise = null
function runRefresh() {
  if (!refreshPromise) {
    refreshPromise = api.post('/supplier/auth/refresh', {}).finally(() => { refreshPromise = null })
  }
  return refreshPromise
}

api.interceptors.request.use((cfg) => {
  cfg.withCredentials = true
  if (cfg.headers) {
    delete cfg.headers.Authorization
    delete cfg.headers.authorization
  }
  return cfg
})
authApi.interceptors.request.use((cfg) => {
  cfg.withCredentials = true
  if (cfg.headers) {
    delete cfg.headers.Authorization
    delete cfg.headers.authorization
  }
  return cfg
})

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const originalRequest = error.config
    if (!originalRequest) return Promise.reject(error)
    if (error.response?.status === 401 && !originalRequest._retry) {
      const url = String(originalRequest.url || '')
      if (
        url.includes('/supplier/auth/refresh') ||
        url.includes('/auth/supplier/login') ||
        url.includes('/supplier/auth/logout') ||
        url.includes('/auth/supplier/register') ||
        url.includes('/auth/supplier/firebase')
      ) {
        return Promise.reject(error)
      }
      if (!shouldAttemptCookieRefresh(error)) {
        return Promise.reject(error)
      }
      originalRequest._retry = true
      try {
        await runRefresh()
        originalRequest.withCredentials = true
        if (originalRequest.headers) {
          delete originalRequest.headers.Authorization
          delete originalRequest.headers.authorization
        }
        return api(originalRequest)
      } catch (refreshError) {
        sessionActive = false
        stripLegacyJwtKeys()
        return Promise.reject(refreshError)
      }
    }
    return Promise.reject(error)
  }
)

class AuthService {
  // Google OAuth login with authorization code
  async googleLoginWithCode(code, redirectUri = null) {
    if (!code || typeof code !== 'string' || !code.trim()) {
      throw new Error('Invalid authorization code received')
    }
    try {
      try {
        const origin = new URL(API_BASE).origin
        await axios.get(`${origin}/health`, { timeout: 8000 })
      } catch (healthErr) {
        // Health is best-effort; continue to OAuth callback
      }

      const response = await authApi.post('/auth/supplier/google/callback', {
        code: code.trim(),
        redirect_uri: redirectUri || window.location.origin + '/supplier/auth/google/callback'
      })

      const { token, refresh_token, user } = response.data
      if (!token || !user) {
        throw new Error('Invalid response from authentication server')
      }

      stripLegacyJwtKeys()
      sessionActive = true
      storeSupplierId(user?.supplier_code)

      return {
        success: true,
        user,
        token
      }
    } catch (error) {
      console.error('Google code exchange error:', error)
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error(
          `Request timed out. Please ensure the backend is running at ${API_BASE} and reachable.`
        )
      }
      if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
        throw new Error('Network error. Please ensure the backend is running at ' + API_BASE)
      }
      throw new Error(
        error.response?.data?.detail || error.message || 'Google authentication failed. Please try again.'
      )
    }
  }

  // Register user
  async register(userData) {
    try {
      // Verify OTP code if provided and not skipping verification
      // Users completing onboarding (skipVerification=true) have already verified in Step2
      if (!userData.skipVerification && userData.verificationCode) {
        try {
          await this.verifyCode(userData.email, userData.verificationCode)
        } catch (verifyError) {
          console.error('OTP verification error:', verifyError)
          const errorMsg = verifyError.response?.data?.detail || verifyError.message || 'Invalid verification code'
          throw new Error(errorMsg.includes('code') || errorMsg.includes('verification') ? errorMsg : 'Invalid verification code. Please check the code and try again.')
        }
      } else if (!userData.skipVerification && !userData.verificationCode) {
        // Only require verification code if not skipping (for direct registration)
        throw new Error('Verification code is required. Please request a code first.')
      }
      // Note: skipVerification=true allows registration without verification code (for onboarding completion)

      // Transform frontend data to backend format (backend expects name, phone_number, location, auth_provider, optional onboarding_data)
      const backendData = {
        email: userData.email,
        password: userData.password ?? (userData.auth_provider === 'email' ? undefined : null),
        auth_provider: userData.auth_provider || 'email',
        name: userData.fullName || userData.businessName || userData.email,
        phone_number: userData.phone || null,
        address: userData.address || null,
        location: userData.country || null
      }
      if (userData.onboarding_data && typeof userData.onboarding_data === 'object') {
        backendData.onboarding_data = userData.onboarding_data
      }

      const response = await authApi.post('/auth/supplier/register', backendData)
      const { token, supplier } = response.data
      const user = supplier

      stripLegacyJwtKeys()
      sessionActive = true
      storeSupplierId(user?.supplier_code || user?.id)

      return {
        success: true,
        user,
        token
      }
    } catch (error) {
      console.error('Registration error:', error)
      
      // Handle validation errors from backend
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail
        
        // If it's an array of validation errors
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => err.msg || err.message).join(', ')
          throw new Error(errorMessages)
        }
        
        // If it's a string
        if (typeof detail === 'string') {
          throw new Error(detail)
        }
      }
      
      // Better error messages based on status code
      if (error.response?.status === 400) {
        throw new Error('Invalid registration data. Please check all fields and try again.')
      } else if (error.response?.status === 409) {
        throw new Error('Email already registered. Please use a different email or try logging in.')
      } else if (error.response?.status === 422) {
        throw new Error('Invalid verification code. Please request a new code and try again.')
      }
      // 500: show backend error if available (e.g. DB table missing)
      const serverError = error.response?.data?.error || error.response?.data?.message
      if (error.response?.status === 500 && serverError) {
        throw new Error(serverError)
      }
      throw new Error(error.message || 'Registration failed. Please try again.')
    }
  }

  // Firebase login (Google/Facebook via Firebase)
  async firebaseLogin(idToken) {
    try {
      const response = await authApi.post('/auth/supplier/firebase', { id_token: idToken })
      const { token, refresh_token, user } = response.data

      stripLegacyJwtKeys()
      sessionActive = true
      storeSupplierId(user?.supplier_code)

      return {
        success: true,
        user,
        token
      }
    } catch (error) {
      console.error('Firebase login error:', error)
      if (error.response?.status === 403) {
        throw new Error(error.response?.data?.message || 'Your account has been blocked.')
      }
      throw new Error(
        error.response?.data?.message ||
        error.response?.data?.detail ||
        'Firebase login failed. Please try again.'
      )
    }
  }

  // Login user — backend uses ONLY suppliers + supplier_profiles; returns supplier with status for redirect
  async login(credentials) {
    try {
      const response = await authApi.post('/auth/supplier/login', credentials)
      
      if (response.data.require_workspace_selection) {
        return {
          success: true,
          require_workspace_selection: true,
          workspaces: response.data.workspaces
        }
      }

      const { token, supplier } = response.data
      const user = {
        ...supplier,
        status: supplier?.status || 'pending',
        is_verified: supplier?.is_verified === true,
        role: supplier?.role || 'supplier',
        supplier_code: supplier?.supplier_code ?? supplier?.id,
      }

      stripLegacyJwtKeys()
      sessionActive = true
      storeSupplierId(user?.supplier_code || user?.id)

      return {
        success: true,
        user,
        token
      }
    } catch (error) {
      console.error('Login error:', error)
      if (isNetworkError(error)) {
        throw new Error(networkErrorMessage('login'))
      }
      // Check if user is blocked (403 Forbidden)
      if (error.response?.status === 403) {
        const blockedReason = error.response?.data?.detail || 'Your account has been blocked.'
        return {
          success: false,
          blocked: true,
          reason: blockedReason,
          error: blockedReason
        }
      }
      // Better error messages based on status code
      if (error.response?.status === 401) {
        throw new Error('Invalid email or password. Please check your credentials and try again.')
      } else if (error.response?.status === 404) {
        throw new Error('Account not found. Please check your email or register a new account.')
      } else if (error.response?.status === 400) {
        const detail = error.response?.data?.detail
        if (typeof detail === 'string') {
          throw new Error(detail)
        }
        throw new Error('Invalid credentials. Please check your email and password.')
      }
      throw new Error(
        error.response?.data?.detail ||
        'Login failed. Please check your credentials and try again.'
      )
    }
  }

  async logout() {
    try {
      await api.post('/supplier/auth/logout', {}, { withCredentials: true })
    } catch (_) {
    } finally {
      sessionActive = false
      stripLegacyJwtKeys()
      clearSupplierLocalData()
    }
  }

  // Get current user (httpOnly cookie session). Supports AbortController for clean unmount.
  async getCurrentUser(options = {}) {
    if (currentUserRequestPromise) {
      return currentUserRequestPromise
    }

    currentUserRequestPromise = this._fetchCurrentUserWithRetry(options).finally(() => {
      currentUserRequestPromise = null
    })

    return currentUserRequestPromise
  }

  async _fetchCurrentUserWithRetry(options, attempt = 1) {
    const { signal } = options
    try {
      const response = await api.get('/supplier/auth/me', {
        timeout: SUPPLIER_ME_TIMEOUT_MS,
        signal,
      })
      return response.data
    } catch (error) {
      if (isRequestAborted(error)) {
        const aborted = new Error('Request aborted')
        aborted.name = 'AbortError'
        aborted.cause = error
        throw aborted
      }

      // Expected when logged out — do not spam console (intercept-console-error)
      if (error.response?.status === 401) {
        return null
      }

      // Check if user is blocked (403 Forbidden)
      if (error.response?.status === 403) {
        const blockedReason = error.response?.data?.detail || 'Your account has been blocked.'
        return {
          is_active: false,
          blocked_reason: blockedReason,
          blocked: true
        }
      }

      // Transient errors: Network error, timeout, 500, 502, 503, 504
      const isTransient =
        isNetworkError(error) ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('timeout') ||
        [500, 502, 503, 504].includes(error.response?.status)

      if (isTransient && attempt <= 3) {
        const delayMs = attempt === 1 ? 500 : attempt === 2 ? 1000 : 2000
        console.warn(`[supplier-auth] getCurrentUser transient error (${error.response?.status || 'Network Error'}). Retrying in ${delayMs}ms (Attempt ${attempt}/3)...`)
        await new Promise(resolve => setTimeout(resolve, delayMs))
        return this._fetchCurrentUserWithRetry(options, attempt + 1)
      }

      console.error('[supplier-auth] getCurrentUser final error after retries:', error)
      throw error
    }
  }

  async refreshToken() {
    try {
      await runRefresh()
      return { success: true }
    } catch (error) {
      console.error('Token refresh error:', error)
      throw error
    }
  }

  // Forgot password – backend at /auth/supplier/forgot-password
  async forgotPassword(email) {
    try {
      const response = await authApi.post('/auth/supplier/forgot-password', { email })
      return response.data
    } catch (error) {
      console.error('Forgot password error:', error)
      throw new Error(
        error.response?.data?.detail ||
        'Failed to send reset email. Please try again.'
      )
    }
  }

  // Reset password – backend at /auth/supplier/reset-password
  async resetPassword(token, newPassword) {
    try {
      const response = await authApi.post('/auth/supplier/reset-password', {
        token,
        new_password: newPassword
      })
      return response.data
    } catch (error) {
      console.error('Reset password error:', error)
      throw new Error(
        error.response?.data?.detail ||
        'Failed to reset password. Please try again.'
      )
    }
  }

  // Send OTP to supplier email (har bar naya OTP generate hota hai backend pe)
  async sendVerificationCode(email) {
    try {
      const response = await authApi.post('/auth/supplier/send-otp', { email })
      return response.data
    } catch (error) {
      console.error('Send OTP error:', error)
      if (isNetworkError(error)) {
        throw new Error(networkErrorMessage('send OTP'))
      }
      throw new Error(
        error.response?.data?.detail ||
        'Failed to send OTP. Please try again.'
      )
    }
  }

  // Verify OTP - sirf jab code generate hue OTP ke barabar ho tab success
  async verifyCode(email, code) {
    try {
      const response = await authApi.post('/auth/supplier/verify-otp', { email, code })
      const data = response.data || {}
      if (data.upload_token) {
        try {
          sessionStorage.setItem('supplier_onboarding_upload_token', data.upload_token)
          localStorage.setItem('supplier_onboarding_upload_token', data.upload_token)
        } catch (_) {}
      }
      return data
    } catch (error) {
      console.error('Verify OTP error:', error)
      if (isNetworkError(error)) {
        throw new Error(networkErrorMessage('verify OTP'))
      }
      const errorMessage = error.response?.data?.detail || 'Invalid OTP. Please try again.'
      throw new Error(errorMessage)
    }
  }

  async startAuthenticatorSetup(email) {
    try {
      const response = await authApi.post('/auth/supplier/2fa/authenticator/start', {
        email: (email || '').trim().toLowerCase(),
      })
      return response.data
    } catch (error) {
      console.error('Authenticator start error:', error)
      if (isNetworkError(error)) {
        throw new Error(networkErrorMessage('start authenticator setup'))
      }
      throw new Error(
        authApiErrorMessage(error, 'Could not start authenticator setup. Please try again.')
      )
    }
  }

  async confirmAuthenticatorSetup(email, code) {
    try {
      const response = await authApi.post('/auth/supplier/2fa/authenticator/confirm', {
        email: (email || '').trim().toLowerCase(),
        code: String(code || '').trim(),
      })
      return response.data
    } catch (error) {
      console.error('Authenticator confirm error:', error)
      if (isNetworkError(error)) {
        throw new Error(networkErrorMessage('confirm authenticator'))
      }
      throw new Error(
        error.response?.data?.detail ||
          'Invalid code or verification failed.'
      )
    }
  }

  async disableAuthenticator2fa(email) {
    try {
      const response = await authApi.post('/auth/supplier/2fa/authenticator/disable', {
        email: (email || '').trim().toLowerCase(),
      })
      return response.data
    } catch (error) {
      console.error('Authenticator disable error:', error)
      if (isNetworkError(error)) {
        throw new Error(networkErrorMessage('disable authenticator'))
      }
      throw new Error(
        error.response?.data?.detail || 'Failed to disable authenticator.'
      )
    }
  }

  /** Change password while logged in: OTP from sendVerificationCode + current password */
  async changePasswordWithOtp(email, currentPassword, newPassword, otp) {
    try {
      const response = await authApi.post('/auth/supplier/change-password', {
        email: (email || '').trim().toLowerCase(),
        current_password: currentPassword,
        new_password: newPassword,
        otp: String(otp || '').trim(),
      })
      return response.data
    } catch (error) {
      console.error('Change password error:', error)
      if (isNetworkError(error)) {
        throw new Error(networkErrorMessage('update password'))
      }
      throw new Error(
        error.response?.data?.detail ||
          'Failed to update password. Please try again.'
      )
    }
  }

  // Check user role by email
  async checkUserRole(email) {
    try {
      const response = await authApi.get('/auth/supplier/check-user-role', { params: { email } })
      return response.data
    } catch (error) {
      console.error('Check user role error:', error)
      throw new Error(
        error.response?.data?.message ||
        error.response?.data?.detail ||
        'Failed to check user role. Please try again.'
      )
    }
  }

  /**
   * Check pending status from supplier_pending_registrations table.
   * If email is in pending table → pending dashboard; when admin approves, row is removed → approved.
   */
  async getPendingStatus(email) {
    try {
      const response = await authApi.get('/auth/supplier/pending-status', { params: { email: (email || '').trim().toLowerCase() } })
      return response.data
    } catch (error) {
      console.error('Pending status check error:', error)
      return { pending: false, status: null }
    }
  }

  setSessionActive(active) {
    sessionActive = !!active
  }

  clearLocalSession() {
    sessionActive = false
    stripLegacyJwtKeys()
    clearSupplierLocalData()
  }

  hasSession() {
    return sessionActive
  }

  isAuthenticated() {
    return sessionActive
  }

  /** @deprecated Phase 3 — use hasSession / AuthContext */
  isSupplierTokenValid() {
    return sessionActive
  }

  /** @deprecated Phase 3 — never returns JWTs */
  getToken() {
    return null
  }

  /** @deprecated Phase 3 — never returns JWTs */
  getRefreshToken() {
    return null
  }

  // Save onboarding progress
  async saveOnboardingProgress(email, currentStep, onboardingData = {}) {
    try {
      const response = await api.put('/supplier/supplier-onboarding/progress', {
        email,
        current_step: currentStep,
        onboarding_data: onboardingData
      })
      return response.data
    } catch (error) {
      console.error('Save onboarding progress error:', error)
      // Don't throw error - progress saving is not critical
      return null
    }
  }

  // Get onboarding progress by email
  async getOnboardingProgress(email) {
    try {
      const response = await api.get(`/supplier/supplier-onboarding/email/${encodeURIComponent(email)}`)
      return response.data
    } catch (error) {
      console.error('Get onboarding progress error:', error)
      // Return null if not found - user can start fresh
      if (error.response?.status === 404) {
        return null
      }
      return null
    }
  }
}

attachSupplierAxiosPerf(api, 'supplier-auth-api')
attachSupplierAxiosPerf(authApi, 'supplier-auth-login')

export default new AuthService()